import type { GuestEventDetail, GuestEventName, RpcArgs } from '@mt/contracts';
import { rpc } from './api.ts';
import { devicePrefs } from './platform.ts';

/**
 * Anonymous usage counts for signed-out visitors (front page, guest practice,
 * sign-up prompts). The server keeps only daily totals — no identifier, cookie
 * or IP address — see supabase/migrations/20260927121700_guest_usage.sql.
 * Browsers that send Do Not Track or Global Privacy Control are not counted.
 * Counting never blocks or breaks the page.
 */

function optedOut(): boolean {
  if (typeof navigator === 'undefined') return true;
  const nav = navigator as Navigator & { globalPrivacyControl?: boolean };
  return nav.globalPrivacyControl === true || nav.doNotTrack === '1';
}

export function countGuestEvent<N extends GuestEventName>(name: N, detail: GuestEventDetail<N>): void {
  if (optedOut()) return;
  // The signature above pairs each event with its allowed details; TypeScript can't carry that through the union.
  void rpc('count_guest_event', { p_name: name, p_detail: detail } as RpcArgs['count_guest_event']).catch(() => {});
}

function today(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Visits count once per device per day, so the totals approximate visitors rather than page loads. */
export function countGuestVisit(name: 'front_page_visit' | 'signup_page_visit'): void {
  const key = `guestCounted:${name}`;
  if (devicePrefs.get<unknown>(key, null) === today()) return;
  devicePrefs.set(key, today());
  countGuestEvent(name, '');
}

/** Remembered on this device so a later sign-up can say whether guest practice came first. */
export function markGuestPracticed(): void {
  devicePrefs.set('guestPracticed', true);
}

export function practicedAsGuest(): boolean {
  return devicePrefs.get<unknown>('guestPracticed', false) === true;
}
